import '../../css/pages/story.css';
import { initShell } from '../lib/shell';

initShell();

const cards = Array.from(document.querySelectorAll<HTMLElement>('.story-card[data-audio]'));
const heroPlayBtn = document.getElementById('story-hero-play') as HTMLButtonElement | null;
const player = document.getElementById('story-player');
const playerArt = document.getElementById('story-player-art') as HTMLImageElement | null;
const playerTitle = document.getElementById('story-player-title');
const playerClose = document.getElementById('story-player-close') as HTMLButtonElement | null;
const playerPlayBtn = document.getElementById('story-player-play') as HTMLButtonElement | null;
const playerSeek = document.getElementById('story-player-seek');
const playerSeekFill = document.getElementById('story-player-seek-fill');
const playerTime = document.getElementById('story-player-time');
const playerVolume = document.getElementById('story-player-volume') as HTMLInputElement | null;
const audioEl = document.getElementById('story-audio-el') as HTMLAudioElement | null;

let activeCard: HTMLElement | null = null;

// Plain SVG elements don't reliably reflect the `hidden` IDL property to
// the `hidden` content attribute (unlike HTMLElement), so both the
// attribute and an explicit inline display are set here rather than
// relying on a [hidden] CSS selector or an empty style.display fallback.
function setIconVisible(el: HTMLElement | null, visible: boolean): void {
  if (!el) return;
  if (visible) {
    el.removeAttribute('hidden');
    el.style.display = 'inline-block';
  } else {
    el.setAttribute('hidden', '');
    el.style.display = 'none';
  }
}

function formatTime(sec: number): string {
  if (!isFinite(sec) || sec < 0) sec = 0;
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

// Works for a .story-card (headphones/equalizer icons) and the page-head
// hero play button (play/pause icons) alike: both mark their two icon
// states with data-icon="idle" / data-icon="active", so this one function
// drives either.
function setSourcePlaying(source: HTMLElement | null, playing: boolean): void {
  if (!source) return;
  source.classList.toggle('is-playing', playing);
  const idleIcon = source.querySelector<HTMLElement>('[data-icon="idle"]');
  const activeIcon = source.querySelector<HTMLElement>('[data-icon="active"]');
  setIconVisible(idleIcon, !playing);
  setIconVisible(activeIcon, playing);
}

function setPlayerPlaying(playing: boolean): void {
  const playIcon = playerPlayBtn?.querySelector<HTMLElement>('.story-player__icon--play');
  const pauseIcon = playerPlayBtn?.querySelector<HTMLElement>('.story-player__icon--pause');
  setIconVisible(playIcon, !playing);
  setIconVisible(pauseIcon, playing);
  playerPlayBtn?.setAttribute(
    'aria-label',
    playing
      ? (playerPlayBtn.dataset.pauseLabel ?? 'Pause')
      : (playerPlayBtn.dataset.playLabel ?? 'Play')
  );
  if (activeCard) setSourcePlaying(activeCard, playing);
}

function updateSeek(): void {
  if (!audioEl) return;
  const dur = audioEl.duration;
  const ratio = dur > 0 ? audioEl.currentTime / dur : 0;
  if (playerSeekFill) playerSeekFill.style.width = `${Math.min(100, ratio * 100)}%`;
  if (playerTime) playerTime.textContent = formatTime(audioEl.currentTime);
}

// `source` is either a .story-card article or the page-head hero play
// button — both carry the same data-audio/data-title/data-image trio, so
// one function drives playback for either and only one plays at a time.
function playStory(source: HTMLElement): void {
  if (!audioEl || !player) return;
  const src = source.dataset.audio;
  if (!src) return;

  const isSameTrack = activeCard === source;

  if (isSameTrack && !audioEl.paused) {
    audioEl.pause();
    setPlayerPlaying(false);
    return;
  }

  if (isSameTrack && audioEl.paused) {
    audioEl.play().catch(() => undefined);
    setPlayerPlaying(true);
    return;
  }

  // Switching to a different story: stop whatever was playing before.
  if (activeCard) setSourcePlaying(activeCard, false);
  activeCard = source;

  const title = source.dataset.title ?? '';
  const image = source.dataset.image ?? '';
  if (playerTitle) playerTitle.textContent = title;
  if (playerArt) {
    playerArt.src = image;
    playerArt.alt = title;
  }

  audioEl.src = src;
  audioEl.currentTime = 0;
  audioEl.volume = playerVolume ? Number(playerVolume.value) : 1;
  player.hidden = false;
  audioEl.play().catch(() => undefined);
  setPlayerPlaying(true);
  if (playerSeekFill) playerSeekFill.style.width = '0%';
  if (playerTime) playerTime.textContent = '0:00';
}

cards.forEach((card) => {
  const btn = card.querySelector<HTMLButtonElement>('[data-role="listen"]');
  btn?.addEventListener('click', () => playStory(card));
});

heroPlayBtn?.addEventListener('click', () => playStory(heroPlayBtn));

playerPlayBtn?.addEventListener('click', () => {
  if (!audioEl) return;
  if (audioEl.paused) {
    audioEl.play().catch(() => undefined);
    setPlayerPlaying(true);
  } else {
    audioEl.pause();
    setPlayerPlaying(false);
  }
});

playerClose?.addEventListener('click', () => {
  if (!audioEl || !player) return;
  audioEl.pause();
  audioEl.removeAttribute('src');
  player.hidden = true;
  if (activeCard) setSourcePlaying(activeCard, false);
  activeCard = null;
});

playerVolume?.addEventListener('input', () => {
  if (!audioEl) return;
  audioEl.volume = Number(playerVolume.value);
});

playerSeek?.addEventListener('click', (ev) => {
  if (!audioEl || !isFinite(audioEl.duration) || !playerSeek) return;
  const rect = playerSeek.getBoundingClientRect();
  const ratio = Math.min(1, Math.max(0, (ev.clientX - rect.left) / rect.width));
  audioEl.currentTime = ratio * audioEl.duration;
});

audioEl?.addEventListener('timeupdate', updateSeek);
audioEl?.addEventListener('loadedmetadata', updateSeek);
audioEl?.addEventListener('ended', () => setPlayerPlaying(false));
